import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Plus, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, inr } from "@/lib/api";
import { PageHeader, Sel, Field, Pill, VerticalBadge } from "@/components/common";

const UserDialog = ({ open, onOpenChange, user, onDone }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(user ? { ...user, password: "" } : { name: "", email: "", phone: "", role: "sales", password: "" }); }, [open, user]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (user) await api.put(`/users/${user.id}`, { ...f, password: f.password || null });
      else await api.post("/users", f);
      toast.success(user ? "Employee updated" : "Employee added"); onOpenChange(false); onDone();
    } catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user ? "Edit employee" : "Add sales employee"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><input data-testid="user-form-name" required className="crm-input" value={f.name || ""} onChange={set("name")} /></Field>
          <Field label="Email / username"><input data-testid="user-form-email" type="email" required className="crm-input" value={f.email || ""} onChange={set("email")} /></Field>
          <Field label="Phone"><input data-testid="user-form-phone" className="crm-input" value={f.phone || ""} onChange={set("phone")} /></Field>
          <Field label="Role"><Sel testid="user-form-role" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={[{ value: "sales", label: "Sales Employee" }, { value: "admin", label: "Sales Admin" }]} /></Field>
          <Field label={user ? "Reset password (optional)" : "Password"}><input data-testid="user-form-password" type="password" required={!user} minLength={6} className="crm-input" value={f.password || ""} onChange={set("password")} /></Field>
          <div className="flex justify-end pt-2"><button data-testid="user-form-submit" className="h-9 rounded-md bg-primary px-5 text-sm font-semibold text-white">Save</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const VerticalTargets = ({ month }) => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const load = useCallback(() => api.get("/vertical-targets", { params: { month } }).then((r) => { setRows(r.data); setEdit({}); }), [month]);
  useEffect(() => { load(); }, [load]);
  const save = async (v) => {
    try { await api.put("/vertical-targets", { vertical: v, month, amount: Number(edit[v] || 0) }); toast.success(`${v} target saved`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="crm-card mb-6" data-testid="vertical-targets">
      <div className="border-b px-4 py-3"><h3 className="text-sm font-bold">Vertical revenue targets — {month}</h3></div>
      <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((r) => (
          <div key={r.vertical} className="rounded-lg border p-4" data-testid={`vertical-target-card-${r.vertical}`}>
            <div className="flex items-center justify-between"><VerticalBadge v={r.vertical} /><span className="num text-sm font-bold text-primary" data-testid={`vertical-target-achievement-${r.vertical}`}>{r.achievement}%</span></div>
            <div className="mt-3 flex items-center gap-1">
              <input data-testid={`vertical-target-input-${r.vertical}`} type="number" min="0" className="crm-input h-8" value={edit[r.vertical] ?? r.target} onChange={(e) => setEdit({ ...edit, [r.vertical]: e.target.value })} />
              {edit[r.vertical] !== undefined && <button data-testid={`vertical-target-save-${r.vertical}`} onClick={() => save(r.vertical)} className="h-8 rounded-md bg-primary px-3 text-xs font-bold text-white">Save</button>}
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-[hsl(var(--brand-accent))]" style={{ width: `${Math.min(100, r.achievement)}%` }} /></div>
            <p className="num mt-2 text-xs text-muted-foreground">Won {inr(r.month_revenue)} · Gap {inr(r.gap)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Team() {
  const { users, loadMeta, user: me } = useAuth();
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [targets, setTargets] = useState([]);
  const [edit, setEdit] = useState({});
  const [dlg, setDlg] = useState(null);
  const loadT = useCallback(() => api.get("/targets", { params: { month } }).then((r) => setTargets(r.data)), [month]);
  useEffect(() => { loadT(); }, [loadT]);
  const tOf = (id) => targets.find((t) => t.user_id === id) || {};
  const toggle = async (u) => {
    try { await api.put(`/users/${u.id}`, { ...u, active: !u.active, password: null }); toast.success(u.active ? "Deactivated" : "Activated"); loadMeta(); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveTarget = async (uid) => {
    try { await api.put("/targets", { user_id: uid, month, amount: Number(edit[uid] || 0) }); toast.success("Target saved"); setEdit({ ...edit, [uid]: undefined }); loadT(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Team" subtitle="Manage sales employees, access and monthly targets">
        <input type="month" data-testid="team-target-month" className="crm-input w-40" value={month} onChange={(e) => setMonth(e.target.value)} />
        <button data-testid="add-employee-button" onClick={() => setDlg({})} className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:opacity-90"><Plus className="h-4 w-4" /> Add employee</button>
      </PageHeader>
      <VerticalTargets month={month} />
      <div className="crm-card overflow-x-auto">
        <table className="crm-table" data-testid="team-table">
          <thead><tr>{["Name", "Email", "Phone", "Role", "Active", `Target (${month})`, "Achieved", "Achievement", "View", ""].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => {
              const t = tOf(u.id);
              return (
                <tr key={u.id} data-testid={`team-row-${u.id}`} className={u.active ? "" : "opacity-50"}>
                  <td className="font-semibold">{u.name}</td><td className="text-xs">{u.email}</td><td className="text-xs">{u.phone}</td>
                  <td><Pill className={u.role === "admin" ? "bg-slate-900 text-white" : "bg-emerald-50 text-emerald-800"}>{u.role === "admin" ? "Admin" : "Sales"}</Pill></td>
                  <td><Switch data-testid={`team-active-toggle-${u.id}`} checked={u.active} disabled={u.id === me.id} onCheckedChange={() => toggle(u)} /></td>
                  <td>{u.role === "sales" ? (
                    <div className="flex items-center gap-1">
                      <input data-testid={`team-target-input-${u.id}`} type="number" className="crm-input h-8 w-32" value={edit[u.id] ?? t.amount ?? 0} onChange={(e) => setEdit({ ...edit, [u.id]: e.target.value })} />
                      {edit[u.id] !== undefined && <button data-testid={`team-target-save-${u.id}`} onClick={() => saveTarget(u.id)} className="h-8 rounded-md bg-slate-900 px-2 text-xs font-bold text-white">Save</button>}
                    </div>) : "—"}</td>
                  <td className="num">{u.role === "sales" ? inr(t.achieved) : "—"}</td>
                  <td className="num">{u.role === "sales" ? `${t.achievement || 0}%` : "—"}</td>
                  <td className="space-x-2 text-xs font-semibold">{u.role === "sales" && <>
                    <Link className="text-primary hover:underline" to={`/dashboard?user=${u.id}`}>Performance</Link>
                    <Link className="text-primary hover:underline" to={`/pipeline?user=${u.id}`}>Pipeline</Link>
                    <Link className="text-primary hover:underline" to={`/activities?user=${u.id}`}>Activity</Link>
                    <Link className="text-primary hover:underline" to={`/eod?user=${u.id}`}>EOD</Link></>}</td>
                  <td><button data-testid={`team-edit-${u.id}`} onClick={() => setDlg(u)} className="rounded p-1 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <UserDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} user={dlg?.id ? dlg : null} onDone={loadMeta} />
    </div>
  );
}
